import type { ReactNode, RefObject, ComponentProps } from "react";
import { createPortal } from "react-dom";
import { Button } from "react-aria-components";
import { MerryChatMinimap, type MerryChatMinimapItem } from "@/shared/ui/MerryChatMinimap";
import { FormalConversationRuler } from "./FormalConversationRuler";
import "./formalConversation.css";

interface FormalConversationViewProps {
  presentation: "workspace" | "temporary-store-overview";
  stickyLevel: 1 | 2 | 3;
  title: string;
  titleRef: RefObject<HTMLElement | null>;
  scrollRef: RefObject<HTMLDivElement | null>;
  titleSentinelRef: RefObject<HTMLDivElement | null>;
  onReset(): void;
  onKeyUp: ComponentProps<"div">["onKeyUp"];
  onMouseUp: ComponentProps<"div">["onMouseUp"];
  onScroll: ComponentProps<"div">["onScroll"];
  items: readonly MerryChatMinimapItem[];
  onNavigate(id: string): void;
  streamStatus: string;
  content: ReactNode;
  composer: ReactNode;
  overlay: ReactNode;
}

/** Owns chat layout while data, actions and the existing composer stay in the runtime. */
export function FormalConversationView({ presentation, stickyLevel, title, titleRef, scrollRef, titleSentinelRef, onReset, onKeyUp, onMouseUp, onScroll, items, onNavigate, streamStatus, content, composer, overlay }: FormalConversationViewProps) {
  const formal = presentation === "workspace";
  const topNavigation = formal ? document.querySelector(".merry-desktop-shell__top-navigation") : null;
  return (
    <section className={`merry-conversation${formal ? " formal-conversation" : ""}`}
      data-conversation-presentation={presentation} data-sticky-level={stickyLevel}
      data-stream-status={streamStatus}
      data-figma-source-node-ids="612:68077,627:75082,612:64954,612:65201,612:65863,631:75507,631:113930,631:115182">
      {topNavigation && stickyLevel >= 2 && createPortal(<header className="merry-conversation__title merry-conversation__title--top" ref={titleRef}>
        <Button aria-label="返回新对话" className="merry-conversation__back" onPress={onReset}>‹</Button>
        <div><span>Merry</span><h1>{title}</h1></div>
      </header>, topNavigation)}
      <div className="merry-conversation__scroll" ref={scrollRef}
        onKeyUp={onKeyUp} onMouseUp={onMouseUp} onScroll={onScroll}>
        <div className={formal ? "formal-conversation__lane" : undefined}>
        {formal && <header className="merry-conversation__title merry-conversation__title--inline" inert={stickyLevel >= 2}>
          <Button aria-label="返回新对话" className="merry-conversation__back" onPress={onReset}>‹</Button>
          <div><span>Merry</span><h1>{title}</h1></div>
        </header>}
        {formal && <div aria-hidden="true" className="merry-conversation__title-sentinel" ref={titleSentinelRef} />}
        {content}
        </div>
      </div>
      {formal ? <FormalConversationRuler items={items} scrollRef={scrollRef} onNavigate={onNavigate} />
        : items.length > 1 ? <MerryChatMinimap accessibleLabel="对话时间轴" className="merry-conversation__minimap" items={items} onNavigate={onNavigate} /> : null}
      <div className={`merry-conversation__composer-area${formal ? " formal-conversation__lane" : ""}`}>{composer}</div>
      {overlay}
    </section>
  );
}
